import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useAppSelector, useAppDispatch } from '../../../../hooks/redux';
import { useDateFsnLocale } from '../../../../hooks/useDateFsnLocale';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import { CustomMUIButton } from '../../../commons/buttons/CustomMUIButton';
import { TextButton, TextButtonClassNames } from '../../../commons/buttons/TextButton';
import { modalSlice } from '../../../../store/reducers/ModalSlice';
import DateVO from '../../../../core/valueObjects/DateVO';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import { AccountStatus, ModalVariants, ButtonClassNames } from '../../../../models/Common';
import { IMultiAccount } from '../../../../models/UserInfo';
import { ApiError } from '../../../../models/ApiError';
import Avatar from '../../../../assets/images/user/Avatar.png';
import AvatarDark from '../../../../assets/images/user/AvatarDark.png';
import { ReactComponent as OpenOptionsIcon } from '../../../../assets/images/multiAccount/openOptionsDots.svg';
import { getErrorMessage } from '../../../../utils';

interface IMobileTableBody {
  item: IMultiAccount;
  handleToggleOptions: (id: number) => void;
  handleDeleteFromAdmin: (id: number) => void;
  handleViewServices: (item: IMultiAccount) => void;
  visibleAction: number;
}

export const MobileTableBody: FC<IMobileTableBody> = ({
  item,
  handleToggleOptions,
  handleViewServices,
  handleDeleteFromAdmin,
  visibleAction,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const menuRef = useRef<HTMLUListElement | null>(null);
  const { openModal } = modalSlice.actions;
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { theme } = useAppSelector((state) => state.appReducer);
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { fnsLocale } = useDateFsnLocale(selectedLanguage);
  const [resendInvitation, { isLoading: isResending }] = userInfoApi.useResendMemberInviteMutation();
  useClickOutside({
    ref: menuRef,
    onClose: () => handleToggleOptions(Infinity),
    enabled: visibleAction === item.id,
  });

  const handleOpenAccountSettings = (userId: number) => {
    dispatch(
      openModal({
        modalVariants: ModalVariants.SubAccountSettings,
        content: { userId },
      }),
    );
  };

  const handleSendAgain = async (userId: number) => {
    try {
      const res = await resendInvitation({ userId }).unwrap();
      const description =
        res.NotifyChannels.sms || res.NotifyChannels.email ? (
          <>
            {t('multiAccount.linkWasSent')}
            <br />
            <br />
            <span style={{ color: 'var(--color-error)' }}>{t('multiAccount.linkIsValidFor', { min: res.ttl })}</span>
            <br />
            <br />
            <span style={{ fontSize: '14px' }}>{t('multiAccount.confirmConnection')}</span>
          </>
        ) : (
          t('multiAccount.mustConfirm')
        );
      dispatch(
        openModal({
          modalVariants: ModalVariants.Success,
          content: {
            title: `${t('success.balanceHasBeenReplenishedSuccessfully')}`,
            description: description as string,
          },
        }),
      );
    } catch (err: unknown) {
      const apiError = err as ApiError;
      if (apiError.status === 400) {
        dispatch(
          openModal({
            modalVariants: ModalVariants.ConfirmationsLimit,
            content: {
              notice: apiError?.data?.error?.message,
            },
          }),
        );
      } else {
        const message = getErrorMessage(err);
        customEnqueueSnackbar(message, 'error');
      }
    }
  };

  return (
    <div className={`multi-account__admin__table-item ${item.Role === 'admin' ? 'admin-item' : ''}`}>
      <div className="multi-account__admin__table-details">
        <div className="multi-account__admin__table-account">
          <div className="multi-account__admin__table-avatar">
            <img src={theme === 'light' ? Avatar : AvatarDark} alt="avatar" />
          </div>
          <div className="multi-account__admin__table-contract">
            {t('multiAccount.account')} <span>{item.UserID}</span>
          </div>
        </div>
        {item.Status === 'verified' && (
          <div className="multi-account__admin__table-address">
            <span>{t('multiAccount.address')}</span>{' '}
            <TextButton
              label={`${item.User?.Address}`}
              handleClick={() => handleViewServices(item)}
              className={TextButtonClassNames.PrimaryActive}
            />
          </div>
        )}
        {item.Status === 'verified' && (
          <div className="multi-account__admin__table-name">
            <span>{t('multiAccount.name')}</span> <p>{item.Alias || item.User?.Name}</p>{' '}
            {item.Role === 'admin' && (
              <span className="multi-account__admin__table-admin-mark">({t('multiAccount.admin')})</span>
            )}
          </div>
        )}
        <div className="multi-account__admin__table-status">
          <span>{t('multiAccount.status')}</span>{' '}
          {item.Status === 'verified' ? (
            <span
              className={`multi-account__admin__table-status-${
                item.Service?.Status === AccountStatus.Active ? 'active' : 'unpaid'
              }`}
            >
              {item.Service?.Status === AccountStatus.Active
                ? `${t('serviceManagement.nextPaymentDate')} ${format(
                    DateVO.create(item.Service?.ExpireDate).get(true),
                    selectedLanguage === 'en' ? 'MMMM d, yyyy' : 'd MMMM yyyy',
                    { locale: fnsLocale },
                  )}`
                : t('multiAccount.noPayment')}
            </span>
          ) : (
            <span className="multi-account__admin__table-awaiting-resend">
              {t('multiAccount.awaitingConfirm')}{' '}
              <TextButton
                label={t('multiAccount.resendConfirm')}
                handleClick={() => handleSendAgain(item.UserID)}
                className={TextButtonClassNames.PrimaryActive}
                isDisabled={isResending}
              />
            </span>
          )}
        </div>
      </div>
      <div className="multi-account__admin__table-tools">
        <CustomMUIButton
          label={<OpenOptionsIcon />}
          handleClick={() => handleToggleOptions(item.id)}
          className={ButtonClassNames.IconGrayFulfilled}
          isDisabled={visibleAction === item.id}
        />
        {visibleAction === item.id && (
          <ul ref={menuRef} className="multi-account__admin__table-controls">
            {item.Role === 'admin' || item.Status === 'verified' ? (
              <li>
                <TextButton
                  label={t('multiAccount.viewServices')}
                  handleClick={() => handleViewServices(item)}
                  className="view-services"
                />
              </li>
            ) : null}
            {item.Role !== 'admin' && item.Status === 'verified' ? (
              <li>
                <TextButton
                  label={t('multiAccount.configure')}
                  handleClick={() => handleOpenAccountSettings(item.UserID)}
                  className="view-services"
                />
              </li>
            ) : null}
            {item.Role !== 'admin' && (
              <li>
                <TextButton
                  label={t('multiAccount.delete')}
                  handleClick={() => handleDeleteFromAdmin(item.UserID)}
                  className={TextButtonClassNames.Red}
                />
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
